import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { PaymentProvider } from '../models/payment-provider';
import { PaymentService } from './payment.service';
import { GiftCard } from '../../giftcard/models/giftcard';
import { ConnectionService } from '../../../services/connection/connection.service';
import { LoggerService } from '../../../services/logger/logger.service';
import { DiscountCoupon } from '../models/discount-coupon';
import { PrepaidPaymentInfo } from '../models/prepaid-payment-info';
import { Location } from '@angular/common';

@Injectable()
export class PaymentOdooService implements PaymentService {

  constructor(
    private connectionService: ConnectionService,
    private logger: LoggerService,
    private location: Location,
  ) { }

  paymentTransaction(orderId: number, paymentProviderId: number): Observable<string> {
    return this._paymentTransaction(
      '/store/payment/transaction/',
      '/payment/return',
      '/payment/cancel',
      orderId,
      paymentProviderId
    );
  }

  paymentGuestTransaction(orderId: number,  guestOrderKey: string, paymentProviderId: number): Observable<string> {
    return this._paymentTransaction(
      '/store/guest-payment/transaction/',
      `/payment/guest/return/${guestOrderKey}/`,
      `/payment/guest/cancel/${guestOrderKey}/`,
      orderId,
      paymentProviderId
    );
  }

  private _paymentTransaction(
    url: string,
    paymentReturnUrl: string,
    paymentCancelUrl: string,
    orderId: number,
    paymentProviderId: number
  ): Observable<string> {
    return this.connectionService.sendJsonPostRequest(url + paymentProviderId, {
      order_id: orderId,
      return_url: `${this.location.prepareExternalUrl(paymentReturnUrl)}`,
      cancel_url: `${this.location.prepareExternalUrl(paymentCancelUrl)}`,
    }).pipe(map(
      result => {
        if (!result) {
          return '';
        }
        if (result.error) {
          this.logger.info(result.error_message);
          return '';
        }
        return result;
      }));
  }

  payOrderByGiftCard(orderId: number): Observable<boolean> {
    return this.connectionService.sendJsonPostRequest('/store/payment/pay_order_by_gift_card/', {
      order_id: orderId
    }).pipe(map(
      result => {
        if (!result) {
          return false;
        }
        if (result.error_message) {
          this.logger.info(result.error_message);
          throw Error(result.error_message);
        }
        return true;
      }));
  }

  guestPayOrderByGiftCard(orderId: number): Observable<string> {
    return this.connectionService.sendJsonPostRequest('/store/payment/guest-pay-order-by-gift-card', {
      order_id: orderId
    }).pipe(map(
      result => {
        if (!result) {
          return '';
        }
        if (result.error_message) {
          this.logger.info(result.error_message);
          throw Error(result.error_message);
        }
        return result.guest_order_key;
      }));
  }

  payOrderByCash(orderId: number): Observable<boolean> {
    return this.connectionService.sendJsonPostRequest('/store/payment/pay_order_by_cash/', {
      order_id: orderId
    }).pipe(map(
      result => {
        if (!result) {
          return false;
        }
        if (result.error_message) {
          this.logger.info(result.error_message);
          return false;
        }
        return true;
      }));
  }

  getPaymentProviders(): Observable<PaymentProvider[]> {
    return this._getPaymentProviders('/store/payment/providers');
  }

  getV21PaymentProviders(): Observable<PaymentProvider[]> {
    return this._getPaymentProviders('/payment-v21/payment/providers');
  }

  private _getPaymentProviders(url: string): Observable<PaymentProvider[]> {
    return this.connectionService.sendJsonPostRequest(url).pipe(map(
      result => {
        if (!result) {
          return [];
        }

        if (result.error_message) {
          this.logger.info(result.error_message);
          return [];
        }

        return result.map(
          paymentProvider => {
            return new PaymentProvider(
              paymentProvider.id,
              paymentProvider.name,
              paymentProvider.paymentName);
          });
      }));
  }

  prepaidOrder(orderNumber: number, giftCardRedeems: GiftCard[], discountCoupon: DiscountCoupon[]): Observable<PrepaidPaymentInfo> {
    return this._prepaidOrder('/store/payment/prepaid_order', orderNumber, giftCardRedeems, discountCoupon);
  }

  prepaidGuestOrder(orderNumber: number, giftCardRedeems: GiftCard[], discountCoupon: DiscountCoupon[]): Observable<PrepaidPaymentInfo> {
    return this._prepaidOrder('/store/payment/prepaid-guest-order', orderNumber, giftCardRedeems, discountCoupon);
  }

  private _prepaidOrder(
    url: string,
    orderNumber: number,
    giftCardRedeems: GiftCard[],
    discountCoupon: DiscountCoupon[]): Observable<PrepaidPaymentInfo> {
    if (!orderNumber) {
      return of(null);
    }

    return this.connectionService.sendJsonPostRequest(url, {
      order_id: orderNumber,
      card_ids: giftCardRedeems ? giftCardRedeems.map(x => x.cardNumber) : [],
      coupons: discountCoupon ? discountCoupon.map(x => x.code) : [],
    }).pipe(map(
      result => {
        if (!result) {
          return null;
        }

        const giftCard = result.gift_card ? result.gift_card.map(x => GiftCard.createFromAny(x)) : null;
        const coupons = result.coupons ? result.coupons.map(x => DiscountCoupon.createFromAny(x)) : null;
        const depositAmount = result.deposit_amount ? result.deposit_amount : 0;

        return new PrepaidPaymentInfo(coupons, giftCard, depositAmount);

      }));
  }
}
